import React, { useRef, useState } from 'react';
import { Book } from '@/utils/bookRouting';
import TopNavigation from '../components/TopNavigation';
import Footer from '../components/Footer';
import { useTheme } from '@/contexts/ThemeContext';
import { openConsentPreferences } from '@/services/consent';

import HeroSection from '../components/landing/HeroSection';
import BookShowcaseSection, {
  BookShowcaseSectionHandle,
} from '../components/landing/BookShowcaseSection';
import VoiceConversationDemo, {
  VoiceConversationDemoHandle,
} from '../components/landing/VoiceConversationDemo';
import QuestionsAnswers, {
  QuestionsAnswersHandle,
} from '../components/landing/QuestionsAnswers';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import { useGSAP } from '@gsap/react';

// Register plugins
gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

interface HomePageProps {
  books: Book[];
  onJoinWaitlist?: () => void;
  onNavigateToExplore?: () => void;
  onNavigateToLibrary?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToMyBooks?: () => void;
  onNavigateHome?: () => void;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToSignup?: (source?: string) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onNavigateToAbout?: () => void;
  onNavigateToPricing?: () => void;
  onToggleMenu: () => void;
  onBookSelect: (bookSlug: string) => void;
  isAuthenticated: boolean;
  isLoading?: boolean;
}

const HomePage: React.FC<HomePageProps> = ({
  books,
  onJoinWaitlist,
  onNavigateToExplore,
  onNavigateToLibrary,
  onNavigateToProfile,
  onNavigateToMyBooks,
  onNavigateHome,
  onNavigateToLogin,
  onNavigateToSignup,
  onNavigateToTerms,
  onNavigateToPrivacy,
  onNavigateToAbout,
  onNavigateToPricing,
  onToggleMenu,
  onBookSelect,
  isAuthenticated,
  isLoading,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Root scope for GSAP
  const mainContainerRef = useRef<HTMLDivElement>(null);

  // Section refs (handles)
  const voiceDemoRef = useRef<VoiceConversationDemoHandle>(null);
  const questionsAnswersRef = useRef<QuestionsAnswersHandle>(null);
  const bookShowcaseRef = useRef<BookShowcaseSectionHandle>(null);

  // Section wrapper refs (scroll triggers)
  const voiceWrapperRef = useRef<HTMLDivElement>(null);
  const questionsAnswersWrapperRef = useRef<HTMLDivElement>(null);
  const bookShowcaseWrapperRef = useRef<HTMLDivElement>(null);

  // State toggles driven by ScrollTrigger
  const [smootherReady, setSmootherReady] = useState(false);
  const [startVoiceDemoVideo, setStartVoiceDemoVideo] = useState(false);
  const [startQuestionsAnswersAnimation, setStartQuestionsAnswersAnimation] =
    useState(false);

  // Initialize ScrollSmoother
  useGSAP(
    () => {
      ScrollSmoother.create({
        wrapper: '#smooth-wrapper',
        content: '#smooth-content',
        smooth: 0.8,
        effects: true,
        smoothTouch: 0.1,
        normalizeScroll: true,
        ignoreMobileResize: true,
      });

      setSmootherReady(true);
    },
    { scope: mainContainerRef }
  );

  // Scroll-triggered animations
  useGSAP(
    () => {
      const voiceHandle = voiceDemoRef.current;
      const qaHandle = questionsAnswersRef.current;
      const booksHandle = bookShowcaseRef.current;

      if (!smootherReady || !voiceHandle || !qaHandle || !booksHandle) return;

      // ---- Voice demo elements ----
      const voiceContainer = voiceHandle.container;
      const voiceHeader = voiceContainer
        ? voiceContainer.querySelectorAll('.main-title')
        : [];
      const voiceParagraph = voiceHandle.paragraph;
      const voiceContent = voiceHandle.content;
      const voiceVideo = voiceHandle.video;

      // ---- Q&A elements ----
      const qaContainer = qaHandle.container;
      const qaHeader = qaContainer ? qaContainer.querySelectorAll('.main-title') : [];
      const qaParagraph = qaHandle.paragraph;
      const qaContent = qaHandle.content;
      const qaQuestion = qaHandle.questionBubble;
      const qaAnswer = qaHandle.answerBubble;

      const qaStepper: (Element | null)[] = [qaQuestion, qaAnswer];
      if (qaContainer) {
        // Vertical stepper desktop
        const verticalStepper = qaContainer.querySelector('.stepper-vertical');
        if (verticalStepper) qaStepper.push(verticalStepper);

        // Horizontal stepper mobile
        const horizontalStepper = qaContainer.querySelector('.stepper-horizontal');
        if (horizontalStepper) qaStepper.push(horizontalStepper);

        // Navigation buttons
        const navButtons = qaContainer.querySelectorAll('.stepper-nav-buttons');
        navButtons.forEach((btn) => qaStepper.push(btn));
      }

      // ---- Book Showcase elements ----
      const booksContainer = booksHandle.container;
      const booksHeader = booksContainer
        ? booksContainer.querySelectorAll('.main-title')
        : [];
      const booksParagraph = booksHandle.paragraph;

      // Initial state
      gsap.set(
        [
          voiceHeader,
          voiceParagraph,
          qaHeader,
          qaParagraph,
          booksHeader,
          booksParagraph,
        ],
        { opacity: 0, y: 50 }
      );

      // Set Q&A bubbles initial position (inwards animation setup)
      if (qaQuestion) gsap.set(qaQuestion, { opacity: 0, x: 50 });
      if (qaAnswer) gsap.set(qaAnswer, { opacity: 0, x: -50 });

      gsap.set(qaStepper, { y: 100 });

      // Set voice video initial position (no opacity fade requested)
      gsap.set(voiceVideo, { y: 100 });

      // Voice Fade/slide in (header + paragraph)
      gsap.to([voiceHeader, voiceParagraph], {
        opacity: 1,
        y: 0,
        stagger: 0.4,
        scrollTrigger: {
          trigger: voiceWrapperRef.current,
          start: 'top 65%',
          end: 'top top',
          scrub: 1,
        },
      });

      // Slide in Voice video
      gsap.to(voiceVideo, {
        y: 0,
        scrollTrigger: {
          trigger: voiceWrapperRef.current,
          start: 'top 50%',
          end: 'top top',
          scrub: 1,
        },
      });

      // Pin Voice section + toggle video
      const voicePin = ScrollTrigger.create({
        trigger: voiceWrapperRef.current,
        start: 'top top',
        end: '+=50%',
        pin: true,
        onEnter: () => setStartVoiceDemoVideo(true),
        onLeaveBack: () => setStartVoiceDemoVideo(false),
      });

      // Fade/translate Voice content out after pin ends
      gsap.to(voiceContent, {
        opacity: 0,
        y: '-=20%',
        scrollTrigger: {
          trigger: voiceWrapperRef.current,
          start: () => voicePin.end,
          end: '+=500',
          scrub: 1,
          immediateRender: false,
        },
      });

      // Q&A Fade/slide in (header + paragraph + bubbles)
      gsap.to([qaHeader, qaParagraph], {
        opacity: 1,
        y: 0,
        stagger: 0.4,
        scrollTrigger: {
          trigger: questionsAnswersWrapperRef.current,
          start: 'top 65%',
          end: 'top top',
          scrub: 1,
        },
      });

      // Slide in AQ Stepper
      gsap.to(qaStepper, {
        y: 0,
        scrollTrigger: {
          trigger: questionsAnswersWrapperRef.current,
          start: 'top 50%',
          end: 'top top',
          scrub: 1,
        },
      });

      if (qaQuestion) {
        gsap.to(qaQuestion, {
          opacity: 1,
          x: 0,
          scrollTrigger: {
            trigger: questionsAnswersWrapperRef.current,
            start: 'top 30%',
            end: 'top 10%',
            scrub: 1,
          },
        });
      }

      if (qaAnswer) {
        gsap.to(qaAnswer, {
          opacity: 1,
          x: 0,
          scrollTrigger: {
            trigger: questionsAnswersWrapperRef.current,
            start: 'top 30%',
            end: 'top 10%',
            scrub: 1,
          },
        });
      }

      // Pin Q&A section + toggle internal animation
      const qaPin = ScrollTrigger.create({
        trigger: questionsAnswersWrapperRef.current,
        start: 'top top',
        end: '+=50%',
        pin: true,
        onEnter: () => setStartQuestionsAnswersAnimation(true),
        onLeaveBack: () => setStartQuestionsAnswersAnimation(false),
      });

      // Fade/translate Q&A content out after pin ends
      const qaExitTl = gsap.timeline({
        scrollTrigger: {
          trigger: questionsAnswersWrapperRef.current,
          start: () => qaPin.end,
          end: '+=500',
          scrub: 1,
          immediateRender: false,
        },
      });

      qaExitTl.to(qaContent, {
        opacity: 0,
        y: '-=20%',
      });

      // Book Showcase Section Fade/slide in (header + paragraph)
      gsap.to([booksHeader, booksParagraph], {
        opacity: 1,
        y: 0,
        stagger: 0.2,
        scrollTrigger: {
          trigger: bookShowcaseWrapperRef.current,
          start: 'top 50%',
          end: 'top top',
          scrub: 1,
        },
      });

      // Use GSAP ticker to defer refresh until the next render cycle, ensuring it's synced with GSAP
      gsap.ticker.add(() => ScrollTrigger.refresh(), true, true);
    },
    // Theme only changes colours, so it isn't a dependency: re-running would
    // stack a second set of pins and tweens on the same elements.
    { scope: mainContainerRef, dependencies: [smootherReady] }
  );

  const themeClasses = isDarkMode
    ? 'text-theme-primary-dark'
    : 'bg-theme-light text-theme-primary-light';

  return (
    <div
      ref={mainContainerRef}
      className={`min-h-screen ${themeClasses} transition-colors duration-300`}
    >
      <div
        id="smooth-wrapper"
        className="min-h-screen"
        style={{
          backgroundColor: isDarkMode
            ? 'var(--background-colour-dark-mode)'
            : 'var(--background-colour-light-mode)',
        }}
      >
        <div className="fixed top-0 left-0 w-full z-50">
          <TopNavigation
            isDarkMode={isDarkMode}
            onToggleMenu={onToggleMenu}
            onNavigateHome={onNavigateHome}
            onNavigateToExplore={onNavigateToExplore}
            onNavigateToLibrary={onNavigateToLibrary}
            onNavigateToLogin={onNavigateToLogin}
            onNavigateToSignup={onNavigateToSignup}
            onNavigateToProfile={onNavigateToProfile}
            onBookSelect={onBookSelect}
            onNavigateToMyBooks={onNavigateToMyBooks}
            showProfileIcon={isAuthenticated}
            isLoading={isLoading}
            isHomePage={true}
          />
        </div>

        <div id="smooth-content" className="px-safe">

          <div className="">
            <HeroSection
              isDarkMode={isDarkMode}
              onNavigateToSignup={onNavigateToSignup}
            />
          </div>

          <div ref={voiceWrapperRef} className="">
            <VoiceConversationDemo
              ref={voiceDemoRef}
              isDarkMode={isDarkMode}
              startAnimation={startVoiceDemoVideo}
            />
          </div>

          <div ref={questionsAnswersWrapperRef} className="">
            <QuestionsAnswers
              ref={questionsAnswersRef}
              isDarkMode={isDarkMode}
              startAnimation={startQuestionsAnswersAnimation}
            />
          </div>

          <div ref={bookShowcaseWrapperRef} className="">
            <BookShowcaseSection
              ref={bookShowcaseRef}
              books={books}
              isDarkMode={isDarkMode}
              onBookSelect={onBookSelect}
              maxBooksToShow={12}
              onNavigateToExplore={onNavigateToExplore}
            />
          </div>

          <Footer
            onNavigateToExplore={onNavigateToExplore}
            onNavigateToLibrary={onNavigateToLibrary}
            onNavigateToSignup={(source) => onNavigateToSignup?.(source)}
            onNavigateToTerms={onNavigateToTerms}
            onNavigateToPrivacy={onNavigateToPrivacy}
            onNavigateToAbout={onNavigateToAbout}
            onNavigateToPricing={onNavigateToPricing}
            onOpenCookiePreferences={openConsentPreferences}
          />
        </div>
      </div>
    </div>
  );
};

export default HomePage;
